import { grailDeliveryLabel } from '../utils/grailDisplay';
import GrailContent from '../components/shop/GrailContent';
import { HgFitment, HgVideos } from '../components/shop/HgContent';
// v291-product-markup-fix
// v275-mobile-contrast
import AsrContent from '../components/shop/AsrContent';
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight, Truck, ShieldCheck, Package, Minus, Plus, ShoppingCart, ChevronLeft, Zap, Wrench, ExternalLink, AlertTriangle, Info, FileText } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { trackEvent } from '../utils/analytics';
import ShopNavigation from '../components/shop/ShopNavigation';
import Footer from '../components/shop/Footer';
import CookieConsent from '../components/shop/CookieConsent';
import NewsletterPopup from '../components/shop/NewsletterPopup';
import { RafflePromoMini } from '../components/raffle/TicketBanner';
import RecentlyViewed from '../components/shop/RecentlyViewed';
import ProductReviews from '../components/shop/ProductReviews';
import FitmentCheck from '../components/shop/FitmentCheck';
import AprCombinableParts from '../components/shop/AprCombinableParts';
import AprStorySections from '../components/shop/AprStorySections';
import NovitecPremium from '../components/shop/NovitecPremium';
import NovitecCrossSell from '../components/shop/NovitecCrossSell';
import PayPalMessage from '../components/shop/PayPalMessage';
import KlarnaInfo from '../components/shop/KlarnaInfo';
import TuevBadge from '../components/shop/TuevBadge';
import VolumeDiscountBadge from '../components/shop/VolumeDiscountBadge';
import RemusSections from '../components/shop/RemusSections';
import RemusHeroSounds from '../components/shop/RemusHeroSounds';
import RemusKonfigurator from '../components/shop/RemusKonfigurator';
import FriedrichKonfigurator from '../components/shop/FriedrichKonfigurator';
import FriedrichVisualPicker from '../components/shop/FriedrichVisualPicker';
import WishlistHeart from '../components/shop/WishlistHeart';
import RegisterCTA from '../components/shop/RegisterCTA';
import { TrustBadges, LiveViewers, StockUrgency, DeliveryEstimate, PaymentIcons } from '../components/shop/ConversionBoosters';
import { cdnImage } from '../utils/cdn';
import { formatDeliveryText } from '../utils/delivery';
import { prepareCsrProduct } from '../utils/csr';
import '../styles/shop.css';
import '../styles/novitec.css';

const API = process.env.REACT_APP_BACKEND_URL;

function formatPrice(price) {
  if (price == null) return '0,00 €';
  return price.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

// Strip inline color/background + fixed width styles from scraped HTML descriptions
// (e.g. HG-Motorsport uses color:#333333 + fixed pixel widths that break mobile/containers)
function sanitizeDescriptionHtml(html) {
  if (!html) return html;
  return html.replace(/\s*color:\s*#[0-9a-fA-F]{3,8};?/gi, '')
             .replace(/\s*background-color:\s*#[0-9a-fA-F]{3,8};?/gi, '')
             .replace(/\s*background:\s*#[0-9a-fA-F]{3,8};?/gi, '')
             // Remove inline width/height pixel declarations so responsive CSS wins
             .replace(/\s*width:\s*\d+(?:\.\d+)?(?:px|pt|cm|in|em|rem)\s*;?/gi, '')
             .replace(/\s*min-width:\s*\d+(?:\.\d+)?(?:px|pt|cm|in|em|rem)\s*;?/gi, '')
             .replace(/\s*height:\s*\d+(?:\.\d+)?(?:px|pt|cm|in|em|rem)\s*;?/gi, '')
             // Remove legacy width/height attributes on tables/images
             .replace(/<(table|td|th|tr|img|col|colgroup)([^>]*?)\swidth\s*=\s*"[^"]*"/gi, '<$1$2')
             .replace(/<(table|td|th|tr|img|col|colgroup)([^>]*?)\swidth\s*=\s*'[^']*'/gi, '<$1$2')
             .replace(/<(table|td|th|tr|img|col|colgroup)([^>]*?)\swidth\s*=\s*\S+/gi, '<$1$2')
             .replace(/<(td|th|tr|img)([^>]*?)\sheight\s*=\s*"[^"]*"/gi, '<$1$2')
             .replace(/<(td|th|tr|img)([^>]*?)\sheight\s*=\s*'[^']*'/gi, '<$1$2')
             .replace(/style="\s*;?\s*"/gi, '');
}

// Build a vehicle-enriched product title for Remus / Bastuck etc.
// "VW Golf 6 GTi 155kW – Sportschalldämpfer..." (when vehicles[0] is present)
// Falls back to plain product.name when there is no structured vehicle.
function buildVehicleEnrichedName(product) {
  if (!product) return '';
  const name = product.name || '';
  const vehicles = Array.isArray(product.vehicles) ? product.vehicles : [];
  if (vehicles.length === 0) return name;
  const v = vehicles[0] || {};
  const marke = (v.marke || '').trim();
  let modell = (v.modell || '').trim();
  const typRaw = (v.typ || '').trim();
  if (!marke || !modell) return name;

  // Strip "(5K1, 1K_)" and date ranges from modell
  modell = modell.replace(/\s*\([^)]*\)/g, '').replace(/\s*\d{2}\/\d{4}-\d{2}\/\d{4}/g, '').trim();
  // Roman numerals -> arabic
  const roman = { ' VIII': ' 8', ' VII': ' 7', ' VI': ' 6', ' IV': ' 4', ' V': ' 5', ' III': ' 3', ' II': ' 2', ' IX': ' 9', ' X': ' 10' };
  Object.entries(roman).forEach(([r, a]) => { modell = modell.replace(r, a); });
  if (modell === modell.toUpperCase()) {
    // capitalise: GOLF -> Golf
    modell = modell.replace(/\b\w+/g, w => w[0] + w.slice(1).toLowerCase());
  }

  // Short type from "2.0 GTi - 155kW - 1984ccm - 4 cyl. - Frontantrieb"
  let typShort = '';
  if (typRaw) {
    const parts = typRaw.split('-').map(s => s.trim());
    const motor = parts[0] || '';
    const kwMatch = typRaw.match(/(\d+)\s*kW/);
    const kw = kwMatch ? kwMatch[1] + 'kW' : '';
    typShort = (motor + (kw ? ' ' + kw : '')).trim();
  }
  const vehicleStr = (marke + ' ' + modell + (typShort ? ' ' + typShort : '')).trim();

  if (!vehicleStr) return name;
  // Avoid duplicate prefixing if name already contains the vehicle string
  if (name.toLowerCase().includes(vehicleStr.toLowerCase())) return name;
  return `${vehicleStr} – ${name}`;
}


// v313b: GARAN-Label Badge (EU-Gewaehrleistung pro Produkt)
function GaranLabelBadge({ alias }) {
  const [url, setUrl] = React.useState(null);
  React.useEffect(() => {
    if (!alias) return;
    const api = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${api}/api/shop/garan-label/${alias}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.image_url) setUrl(d.image_url); })
      .catch(() => {});
  }, [alias]);
  if (!url) return null;
  return (
    <div data-testid="garan-label-badge" style={{
      marginTop: 16, padding: 12, borderRadius: 8,
      background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.25)',
      display: 'flex', alignItems: 'center', gap: 12
    }}>
      <img src={url} alt="Gewaehrleistungs-Label" style={{ height: 56, borderRadius: 4 }} />
      <div style={{ fontSize: 13, color: '#93c5fd' }}>
        Gesetzliche Gewaehrleistung nach EU-Recht.{' '}
        <a href="/rechtliches/gewaehrleistung" style={{ color: '#60a5fa' }}>Details</a>
      </div>
    </div>
  );
}

export default function ProductDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('description');
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [grailInstall, setGrailInstall] = useState(false);
  useEffect(() => setGrailInstall(false), [slug]);
  const [installOpen, setInstallOpen] = useState(false);
  const [installForm, setInstallForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [installSent, setInstallSent] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ name: '', email: '', phone: '', company: '', message: '', vehicle_info: '' });
  const [quoteSent, setQuoteSent] = useState(false);
  const [vin, setVin] = useState('');
  const [vinError, setVinError] = useState('');
  const [selectedSetOptions, setSelectedSetOptions] = useState({});
  const [selectedAusf, setSelectedAusf] = useState({});
  const [variantError, setVariantError] = useState('');
  const [modernBrands, setModernBrands] = useState([]);
  const [premiumBrands, setPremiumBrands] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/shop/design-styles`)
      .then(r => r.ok ? r.json() : { modern: [], premium: [] })
      .then(d => {
        setModernBrands(Array.isArray(d.modern) ? d.modern.map(n => (n || '').toUpperCase()) : []);
        setPremiumBrands(Array.isArray(d.premium) ? d.premium.map(n => (n || '').toUpperCase()) : []);
      })
      .catch(() => { setModernBrands([]); setPremiumBrands([]); });
  }, []);

  useEffect(() => {
    setLoading(true);
    setSelectedSetOptions({});
    setSelectedAusf({});
    fetch(`${API}/api/shop/product-by-slug/${slug}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(data => {
        setProduct(prepareCsrProduct(data)); setSelectedImage(0); setSelectedVariant(null);
        // Analytics: track product_view
        try {
          trackEvent('product_view', {
            slug: data?.slug || slug,
            name: data?.name,
            price: data?.price,
            brand: data?.manufacturer || data?.brand,
            han: data?.han,
            category: data?.category_type,
          });
        } catch {}
        // Track recently viewed (server-side if logged in)
        if (token && data?.alias) {
          fetch(`${API}/api/auth/me/recently-viewed/${data.alias}`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` }
          }).catch(() => {});
        }
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug, token]);

  const getSelectedOptionsForCart = () => {
    if (product?.grail_managed) return (grailSelected?.grail_installation_included || grailInstall) && Number.isInteger(grailSelected?.grail_installation_price_cents) ? [{ id: 'grail-installation', name: 'Einbau bei TrXL Performance' }] : [];
    const opts = [];
    (product?.product_sets || []).forEach((set, i) => {
      (set.options || []).forEach((opt, j) => {
        if (selectedSetOptions[`${i}-${j}`]) {
          opts.push({ name: opt.name, price: opt.price || 0, set_label: set.label });
        }
      });
    });
    // Ausführung/Zulassung-Varianten (APR/Stoll Configurator-Optionen)
    Object.entries(selectedAusf).forEach(([grp, val]) => {
      if (val) opts.push({ name: val, price: 0, set_label: grp });
    });
    return opts.length > 0 ? opts : null;
  };

  // SEO Meta Tags + Schema.org JSON-LD
  useEffect(() => {
    if (!product) return;
    const enrichedName = buildVehicleEnrichedName(product);
    // If we have a vehicle-enriched title (REMUS etc.), prefer it over a stale
    // manual seo_title that may not contain marke/modell — keeps Browser-Tab,
    // og:title and SSR consistent.
    const hasVehiclePrefix = enrichedName && enrichedName !== (product.name || '');
    const effectiveTitle = product.asr_managed && product.seo_title ? product.seo_title : hasVehiclePrefix
      ? enrichedName
      : (product.seo_title || enrichedName || product.name || 'TrXL Performance');
    document.title = effectiveTitle;
    const setMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(name.startsWith('og:') ? 'property' : 'name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', product.seo_description || product.description?.replace(/<[^>]*>/g, '').substring(0, 160));
    setMeta('keywords', product.seo_keywords || '');
    setMeta('og:title', effectiveTitle);
    setMeta('og:description', product.seo_description || product.description?.replace(/<[^>]*>/g, '').substring(0, 160));
    setMeta('og:type', 'product');
    if (product.image_url) setMeta('og:image', product.image_url);

    // v291: Client-JSON-LD entfernt. SSR liefert bereits Product+Offer und
    // eine 2. Insertion fuehrt bei Google zu widerspruechlichen Feldern.
    // Falls ein alter Script-Tag aus vorheriger Navigation noch existiert -> entfernen.
    const _oldSchema = document.getElementById('product-jsonld');
    if (_oldSchema && _oldSchema.dataset.source !== 'ssr') _oldSchema.remove();

    return () => {
      document.title = 'TrXL Performance Shop';
      const el = document.getElementById('product-jsonld');
      if (el) el.remove();
    };
  }, [product]);

  // Parse multi-attribute variants into separate groups
  // ONLY for products with structured variation_options (from JTL import)
  const variantAttributes = useMemo(() => {
    if (!product?.variants?.length) return [];
    const attrMap = {};
    product.variants.forEach(v => {
      // ONLY use structured variation_options with attribute field
      if ((v.grail_managed ? (v.grail_variation_options || v.variation_options) : v.variation_options)?.length > 0) {
        (v.grail_managed ? (v.grail_variation_options || v.variation_options) : v.variation_options).forEach(opt => {
          const key = opt.attribute || opt.group || 'Option';
          const value = opt.value || '';
          if (value) {
            if (!attrMap[key]) attrMap[key] = new Set();
            attrMap[key].add(value);
          }
        });
        return;
      }
      // Fallback: parse variation_value string "Key: Value | Key: Value"
      const val = v.variation_value || v.variation_label || '';
      const parts = val.split('|').map(p => p.trim()).filter(Boolean);
      parts.forEach(part => {
        const colonIdx = part.indexOf(':');
        if (colonIdx > 0) {
          const key = part.substring(0, colonIdx).trim();
          const value = part.substring(colonIdx + 1).trim();
          if (!attrMap[key]) attrMap[key] = new Set();
          attrMap[key].add(value);
        }
      });
    });
    return Object.entries(attrMap).map(([key, values]) => ({ key, values: [...values] }));
  }, [product?.variants]);

  const isMultiAttribute = variantAttributes.length > 1;
  const [selectedAttrs, setSelectedAttrs] = useState({});

  const matchedVariant = useMemo(() => {
    if (!isMultiAttribute || !product?.variants?.length) return selectedVariant;
    const attrKeys = variantAttributes.map(a => a.key);
    const allSelected = attrKeys.every(k => selectedAttrs[k]);
    if (!allSelected) return null;
    return product.variants.find(v => {
      if ((v.grail_managed ? (v.grail_variation_options || v.variation_options) : v.variation_options)?.length > 0) {
        return attrKeys.every(k => {
          const opt = (v.grail_managed ? (v.grail_variation_options || v.variation_options) : v.variation_options).find(o => (o.attribute || o.group) === k);
          return opt && opt.value === selectedAttrs[k];
        });
      }
      const val = v.variation_value || v.variation_label || '';
      if (val && val.includes(':')) {
        return attrKeys.every(k => val.includes(`${k}: ${selectedAttrs[k]}`));
      }
      return false;
    }) || null;
  }, [isMultiAttribute, selectedAttrs, product?.variants, variantAttributes, selectedVariant]);

  useEffect(() => {
    if (isMultiAttribute && matchedVariant) setSelectedVariant(matchedVariant);
  }, [matchedVariant, isMultiAttribute]);

  // --- Variant <-> URL Sync (?variant=alias) ---
  // 1) Beim Laden des Produkts: falls ?variant=... vorhanden, Variante autoselektieren.
  useEffect(() => {
    if (!product?.variants?.length) return;
    const params = new URLSearchParams(location.search);
    const wantedAlias = params.get('variant') || product.asr_selected_alias;
    if (!wantedAlias) return;
    const v = product.variants.find(x => x.alias === wantedAlias);
    if (!v) return;
    setSelectedVariant(v);
    // Multi-Attribute: auch die einzelnen Dropdowns setzen
    if (isMultiAttribute && (v.grail_managed ? (v.grail_variation_options || v.variation_options) : v.variation_options)?.length > 0) {
      const attrs = {};
      (v.grail_managed ? (v.grail_variation_options || v.variation_options) : v.variation_options).forEach(opt => {
        const key = opt.attribute || opt.group || 'Option';
        if (opt.value) attrs[key] = opt.value;
      });
      setSelectedAttrs(prev => ({ ...prev, ...attrs }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.alias]);

  // 2) Bei Variantenwechsel: URL aktualisieren (replaceState -> keine History-Spam)
  useEffect(() => {
    if (!product?.alias) return;
    const params = new URLSearchParams(location.search);
    const currentVariantInUrl = params.get('variant') || '';
    const nextVariant = selectedVariant?.alias || '';
    if (currentVariantInUrl === nextVariant) return;
    if (nextVariant) {
      params.set('variant', nextVariant);
    } else {
      params.delete('variant');
    }
    const qs = params.toString();
    const newUrl = `/p/${product.slug || product.alias}${qs ? `?${qs}` : ''}${location.hash || ''}`;
    window.history.replaceState({}, '', newUrl);
    // Canonical-Tag aktualisieren (Google indexiert weiterhin die Basis-URL)
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement('link');
      canon.setAttribute('rel', 'canonical');
      document.head.appendChild(canon);
    }
    canon.setAttribute('href', `${window.location.origin}/p/${product.slug || product.alias}`);
    // Existing ASR SEO titles remain authoritative on every established URL.
    if (product.asr_managed || product.grail_managed) return;
    // Title anreichern damit User in Tabs die Variante sieht
    if (selectedVariant) {
      const label = getVariantLabel(selectedVariant);
      if (label && document.title && !document.title.includes(label)) {
        document.title = `${product.name} - ${label}`;
      }
    } else if (product.name) {
      document.title = product.name;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant?.alias, product?.alias]);

  if (loading) return (
    <div className="shop-page"><ShopNavigation />
      <div className="product-detail-skeleton" style={{ paddingTop: 100, textAlign: 'center', color: 'var(--fg-muted)' }}>Laden...</div>
      <Footer />
    </div>
  );

  if (!product) return (
    <div className="shop-page"><ShopNavigation />
      <div style={{ paddingTop: 120, textAlign: 'center', minHeight: '60vh' }}>
        <h1 style={{ color: '#fff', fontSize: '2rem', marginBottom: 16 }}>Produkt nicht gefunden</h1>
        <Link to="/shop" style={{ color: 'var(--primary)' }}>Zurück zum Shop</Link>
      </div>
      <Footer />
    </div>
  );

  // v312a-fix-18 allImages-typefilter: nur echte Strings zulassen, verhindert cdnImage-Crash
  const grailSelected = product.grail_has_variants ? (isMultiAttribute ? matchedVariant : selectedVariant) : product;
  const grailContent = grailSelected?.grail_content || product.grail_content;
  const grailDelivery = grailDeliveryLabel( product.grail_has_variants && !grailSelected ? "Bitte Ausführung wählen" : grailSelected?.grail_available === false ? "Zurzeit nicht lieferbar" : grailContent?.delivery || grailSelected?.delivery_time || "Lieferzeit auf Anfrage");
  const asrImageVariant = product.asr_has_variants ? (isMultiAttribute ? matchedVariant : selectedVariant) : null;
  const allImages = [...new Set((product.grail_managed && grailContent?.images?.length ? grailContent.images : asrImageVariant ? [asrImageVariant.image_url_full, ...(asrImageVariant.gallery_urls_full || [])] : [product.image_url, ...(product.gallery_urls || [])]).filter(u => typeof u === 'string' && u))].map(cdnImage);
  const currentImg = allImages[selectedImage] || '/images/placeholder.svg';
  const APR_FAMILY = ['APR', 'DINAN', 'HJS', 'STOLL SPORT'];
  const brandUpper = (product.manufacturer || '').toUpperCase();
  const isAprFamily = APR_FAMILY.includes(brandUpper);
  // Robuste Premium-Erkennung: matcht manufacturer, brand ODER categories
  const premiumCandidates = [product.manufacturer, product.brand, ...(product.categories || [])]
    .map((x) => (x || '').toUpperCase());
  const isPremiumBrand = premiumBrands.length > 0 && premiumBrands.some((pb) => premiumCandidates.includes(pb));
  const useModernStory = !product.grail_managed && !isPremiumBrand && (isAprFamily || modernBrands.includes(brandUpper));
  const categoryPath = product.categories?.[0]?.split('/').map((p, i, arr) => ({
    name: p.replace(/__/g, ' / ').replace(/_/g, ' '),
    value: arr.slice(0, i + 1).join('/'),
  })) || [];

  const hgVariant = isMultiAttribute ? matchedVariant : selectedVariant;
  const asrContent = hgVariant?.asr_content || (!product.asr_has_variants ? product.asr_content : null);
  const hgContent = hgVariant ? hgVariant.hg_content : product.hg_content;
  const hgHasFitment = product.hg_content?.fitment_html || hgContent?.fitment_html;
  const hgHasVideos = !product.grail_managed && (product.hg_content?.videos?.length || hgContent?.videos?.length);
  const asrDelivery = product.asr_has_variants ? (hgVariant?.asr_available === false ? 'Zurzeit nicht lieferbar' : hgVariant ? (hgVariant.asr_content?.delivery || hgVariant.delivery_time || 'Lieferzeit auf Anfrage') : 'Bitte Ausführung wählen') : (product.asr_available === false ? 'Zurzeit nicht lieferbar' : product.asr_content?.delivery || product.delivery_time || 'Lieferzeit auf Anfrage');
  const variantDisplay = product.variant_display || 'dropdown';
  const currentPrice = product.grail_managed ? (grailSelected?.price ?? product.price) + ((grailSelected?.grail_installation_included || grailInstall) && Number.isInteger(grailSelected?.grail_installation_price_cents) ? grailSelected.grail_installation_price_cents / 100 : 0) : selectedVariant?.price || product.price;

  // Build variant label from structured variation_options
  const getVariantLabel = (v) => {
    // Use structured variation_options if available
    if ((v.grail_managed ? (v.grail_variation_options || v.variation_options) : v.variation_options)?.length > 0) {
      return (v.grail_managed ? (v.grail_variation_options || v.variation_options) : v.variation_options)
        .map(opt => opt.value || opt.raw_value)
        .filter(Boolean)
        .join(' | ');
    }
    // Fallback: use variation_value (cleaned combined string)
    if (v.variation_value) {
      return v.variation_value;
    }
    // Last fallback: alias-based label
    return v.alias?.replace(product.alias + '-', 'Variante ') || v.alias;
  };

  const handleInstallInquiry = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('product_slug', product.slug);
      formData.append('product_name', product.name);
      formData.append('message', `Einbau-Anfrage: ${installForm.message}`);
      formData.append('customer_name', installForm.name);
      formData.append('customer_email', installForm.email);
      formData.append('customer_phone', installForm.phone);
      formData.append('vehicle_info', '');
      await fetch(`${API}/api/fitment/check`, { method: 'POST', body: formData });
      setInstallSent(true);
    } catch {}
  };

  const handleQuoteRequest = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/quotes/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_alias: product.alias || product.slug,
          product_name: product.name,
          quantity,
          ...quoteForm,
        }),
      });
      if (res.ok) setQuoteSent(true);
    } catch {}
  };

  const displayProduct = product.grail_managed ? (grailSelected || product) : product.asr_managed && hgVariant ? hgVariant : product;

  // Custom fields
  const customFields = [];
  for (let i = 1; i <= 5; i++) {
    const label = displayProduct[`custom_field_${i}_label`];
    const value = displayProduct[`custom_field_${i}_value`];
    if (label && value) customFields.push({ label, value });
  }

  // v312a-fix-35: Nicht lieferbare Artikel (CSR Bestand=0) nicht bestellbar.
  const notAvailable = product.grail_managed ? (!grailSelected || grailSelected.grail_available !== true) : product.asr_managed
    ? (displayProduct.asr_available === false || /nicht\s+lieferbar/i.test(displayProduct.asr_content?.delivery || displayProduct.delivery_time || ''))
    : (product.csr_display?.orderable === false || product.csr_available === false || /nicht\s+lieferbar/i.test(product.delivery_time || ''));

  return (
    <div className="shop-page" data-testid="product-detail-page">
      <ShopNavigation />

      <div className="pd-container">
        {/* Breadcrumb */}
        <nav className="breadcrumb" data-testid="pd-breadcrumb">
          <Link to="/">Start</Link>
          <ChevronRight size={12} />
          <Link to="/shop">Shop</Link>
          {categoryPath.slice(0, 3).map((c, i) => (
            <React.Fragment key={c.value}>
              <ChevronRight size={12} />
              <Link to={`/shop?category=${c.value}`}>{c.name}</Link>
            </React.Fragment>
          ))}
          <ChevronRight size={12} />
          <span>{product.name?.substring(0, 40)}{product.name?.length > 40 ? '...' : ''}</span>
        </nav>

        {/* Main Product Section */}
        <div className="pd-main">
          {/* Image Gallery */}
          <div className="pd-gallery" data-testid="pd-gallery">
            <div className="pd-main-image">
              <img src={currentImg} alt={product.name} onError={e => { e.target.src = '/images/placeholder.svg'; }} />
              {allImages.length > 1 && (
                <>
                  <button className="img-nav prev" onClick={() => setSelectedImage(i => (i - 1 + allImages.length) % allImages.length)}><ChevronLeft size={20} /></button>
                  <button className="img-nav next" onClick={() => setSelectedImage(i => (i + 1) % allImages.length)}><ChevronRight size={20} /></button>
                </>
              )}
            </div>
            {allImages.length > 1 && (
              <div className="pd-thumbnails">
                {allImages.slice(0, 8).map((img, i) => (
                  <button key={i} className={`pd-thumb ${i === selectedImage ? 'active' : ''}`} onClick={() => setSelectedImage(i)}>
                    <img src={img} alt="" onError={e => { e.target.style.display = 'none'; }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="pd-info" data-testid="pd-info">
            {product.manufacturer && (
              <Link to={`/shop?manufacturer=${product.manufacturer}`} className="pd-brand">{product.manufacturer}</Link>
            )}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
              <h1 className="pd-title" data-testid="pd-title" style={{ flex: 1 }}>{buildVehicleEnrichedName(displayProduct)}</h1>
              <WishlistHeart alias={product.alias} size={22} />
            </div>

            {displayProduct.manufacturer_sku && <p className="pd-sku">Hersteller-Art.-Nr.: {displayProduct.manufacturer_sku}</p>}
            {product.alias && <p className="pd-sku" style={{ fontSize: '0.78rem' }}>Interne Art.-Nr.: {displayProduct.alias}</p>}

            {/* Price */}
            <div className="pd-price-block" data-testid="pd-price">
              <span className="pd-price">{formatPrice(currentPrice)}</span>
              {product.compare_price > 0 && product.compare_price !== currentPrice && (
                <span className="pd-compare-price">{formatPrice(product.compare_price)}</span>
              )}
              <span className="pd-tax">inkl. MwSt. zzgl. Versandkosten</span>
            </div>
            {/* v226: TÜV/ABE/ECE-Vertrauens-Badges direkt unter Preis */}
            <div style={{ margin: '4px 0 8px' }}>
              <TuevBadge product={product} data-testid="pd-tuev-badges" />
            </div>
            <PayPalMessage amount={currentPrice} placement="product" boxed className="pd-paypal-msg" />
            <KlarnaInfo className="pd-klarna-msg" amount={currentPrice} placement="product" />
            {/* v226: Volumen-Rabatt-Hinweis (nur wenn Preis nahe/über 2500€) */}
            <VolumeDiscountBadge subtotal={currentPrice} placement="product" />

            {/* Delivery + Legal Info */}
            <div className="pd-meta">
              {(
                <div className="pd-meta-item" data-testid="pd-delivery-time"><Truck size={16} /> Lieferzeit: {(product.grail_managed ? grailDelivery : product.csr_display?.delivery || (product.asr_managed ? asrDelivery : formatDeliveryText(product.delivery_time, product.stock)))}</div>
              )}
              {!product.grail_managed && !product.csr_display && !product.asr_managed && product.stock > 0 && (
                <div className="pd-meta-item">
                  <Package size={16} /> {product.stock} auf Lager
                </div>
              )}
              <div className="pd-meta-item"><ShieldCheck size={16} /> Sichere Zahlung</div>
              {!product.grail_managed && product.in_stock_from_grail && (<div className="pd-meta-item" data-testid="pd-grail-in-stock" style={{ color: "#22c55e", fontWeight: 600 }}><Zap size={16} /> Sofort lieferbar ab Grail-Lager (2-14 Werktage)</div>)}
              {displayProduct.legal_notice && (
                <div className="pd-meta-item pd-legal"><AlertTriangle size={16} /> {displayProduct.legal_notice}</div>
              )}
              {displayProduct.approval_info && (
                <div className="pd-meta-item"><FileText size={16} /> Zulassung: {displayProduct.approval_info}</div>
              )}
            </div>

            {/* Custom Fields as Product Attributes */}
            {customFields.length > 0 && (
              <div className="pd-attributes" data-testid="pd-attributes">
                {customFields.map((cf, i) => (
                  <div key={i} className="pd-attribute-item">
                    <span className="pd-attribute-label">{cf.label}</span>
                    <span className="pd-attribute-value">{cf.value}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Variants - Separate dropdowns for multi-attribute, single for simple */}
            {product.variants?.length > 0 && (() => {
              const isFriedrichMultiDim = isMultiAttribute
                && (product.manufacturer === 'Friedrich Motorsport' || product.brand === 'Friedrich Motorsport')
                && (product.variation_attributes || []).includes('Endrohr')
                && (product.variation_attributes || []).includes('Material');
              return (
              <div className="pd-variants" data-testid="pd-variants">
                {isFriedrichMultiDim ? (
                  <FriedrichVisualPicker
                    product={product}
                    selectedAttrs={selectedAttrs}
                    onSelect={(key, val) => {
                      setSelectedAttrs(prev => ({ ...prev, [key]: val }));
                      setVariantError('');
                    }}
                    matchedVariant={matchedVariant}
                  />
                ) : isMultiAttribute ? (
                  <>
                    {variantAttributes.map((attr, attrIdx) => {
                      // Filter values: only show options that have a matching variant
                      // considering all OTHER selected attributes
                      const otherSelected = Object.entries(selectedAttrs).filter(([k, v]) => k !== attr.key && v);
                      const availableValues = attr.values.filter(val => {
                        if (otherSelected.length === 0) return true;
                        // Check if there's at least one variant matching this value + other selections
                        return product.variants.some(v => {
                          const opts = (v.grail_managed ? (v.grail_variation_options || v.variation_options) : v.variation_options) || [];
                          const varVal = v.variation_value || '';
                          // Check this attribute matches
                          let thisMatch = false;
                          if (opts.length > 0) {
                            const opt = opts.find(o => (o.attribute || o.group) === attr.key);
                            thisMatch = opt && opt.value === val;
                          } else if (varVal.includes(':')) {
                            thisMatch = varVal.includes(`${attr.key}: ${val}`);
                          }
                          if (!thisMatch) return false;
                          // Check all other selected attributes match
                          return otherSelected.every(([k, sv]) => {
                            if (opts.length > 0) {
                              const opt = opts.find(o => (o.attribute || o.group) === k);
                              return opt && opt.value === sv;
                            }
                            return varVal.includes(`${k}: ${sv}`);
                          });
                        });
                      });
                      return (
                      <div key={attr.key} className="pd-variant-group">
                        <label className="pd-variant-label">{attr.key}:</label>
                        <select
                          className="variant-dropdown"
                          data-testid={`variant-select-${attr.key}`}
                          value={selectedAttrs[attr.key] || ''}
                          onChange={e => { setSelectedAttrs(prev => ({ ...prev, [attr.key]: e.target.value })); setVariantError(''); }}
                        >
                          <option value="">Auswählen</option>
                          {availableValues.map(val => (
                            <option key={val} value={val}>{val}</option>
                          ))}
                        </select>
                      </div>
                      );
                    })}
                  </>
                ) : (
                  <>
                    <label className="pd-variant-label">Variante:</label>
                    <select
                      className="variant-dropdown"
                      data-testid="variant-select"
                      value={selectedVariant?.alias || ''}
                      onChange={e => {
                        const v = product.variants.find(v => v.alias === e.target.value);
                        setSelectedVariant(v || null);
                        setVariantError('');
                      }}
                    >
                      <option value="">— Bitte wählen —</option>
                      {product.variants.map(v => (
                        <option key={v.alias} value={v.alias}>
                          {getVariantLabel(v)}{v.price > 0 && v.price !== product.price ? ` — ${formatPrice(v.price)}` : ''}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
              );
            })()}

            {/* Scraped Configurator Options (from source site) */}
            {product.variants_raw?.length > 0 && !product.variants?.length && (
              <div className="pd-variants" data-testid="pd-scraped-variants">
                {product.variants_raw.map((vr, i) => (
                  <div key={i} className="pd-variant-group">
                    <label className="pd-variant-label">{vr.label}:</label>
                    <select className="variant-dropdown" data-testid={`scraped-variant-${i}`} defaultValue="">
                      <option value="">— Bitte wählen —</option>
                      {vr.options?.map((opt, j) => (
                        <option key={j} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}

            {/* Ausführung / Zulassungs-Variante (APR/Stoll Configurator-Optionen) */}
            {!product.grail_managed && product.variant_options && Object.keys(product.variant_options).length > 0 && (
              <div className="pd-variant-options" data-testid="pd-variant-options"
                   style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {Object.entries(product.variant_options).map(([grp, opts], gi) => (
                  <div key={gi}>
                    <label style={{ display: 'block', color: '#ccc', fontSize: '0.85rem', fontWeight: 600, marginBottom: 6 }}>
                      {grp}
                    </label>
                    <select data-testid={`pd-variant-select-${gi}`}
                      value={selectedAusf[grp] || ''}
                      onChange={e => setSelectedAusf(prev => ({ ...prev, [grp]: e.target.value }))}
                      style={{
                        width: '100%', padding: '12px 14px', borderRadius: 8,
                        background: 'rgba(255,255,255,0.05)', color: '#fff',
                        border: '1px solid rgba(255,255,255,0.15)', fontSize: '0.9rem', cursor: 'pointer',
                      }}>
                      <option value="" style={{ background: '#1a1a1a' }}>Bitte wählen…</option>
                      {(opts || []).map((o, oi) => (
                        <option key={oi} value={o} style={{ background: '#1a1a1a' }}>{o}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}

            {product.grail_managed && !product.grail_legacy_installation_variants && <div className="grail-installation">
              <label htmlFor="grail-installation-select">Einbau</label>
              <select id="grail-installation-select" value={(grailSelected?.grail_installation_included || grailInstall) && Number.isInteger(grailSelected?.grail_installation_price_cents) ? 'yes' : 'no'} onChange={e => setGrailInstall(e.target.value === 'yes')}>
                <option value="no">Ohne Einbau</option>
                {Number.isInteger(grailSelected?.grail_installation_price_cents) && <option value="yes">Einbau bei TrXL Performance (+{formatPrice(grailSelected.grail_installation_price_cents / 100)})</option>}
              </select>
              {(grailSelected?.grail_installation_included || grailInstall) && Number.isInteger(grailSelected?.grail_installation_price_cents) && <p>Einbau nach Terminvereinbarung in Bruchhausen-Vilsen. Für diesen Warenkorb ist nur Selbstabholung möglich.</p>}
            </div>}
            {/* Product Sets / Accessories (Steuerung, Endrohre, Montageservice etc.) */}
            {!product.grail_managed && product.product_sets?.length > 0 && (
              <div className="pd-product-sets" data-testid="pd-product-sets" style={{ marginTop: '0.5rem' }}>
                {product.product_sets.map((set, i) => (
                  <div key={i} className="pd-set-group" style={{
                    marginBottom: 8, borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
                    background: 'rgba(255,255,255,0.03)', overflow: 'hidden'
                  }}>
                    <div style={{
                      padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8,
                      color: '#fff', fontWeight: 600, fontSize: '0.9rem',
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                    }}>
                      <span style={{ flex: 1 }}>{set.label}</span>
                      {set.optional && <span style={{ padding: '2px 8px', borderRadius: 4, background: 'rgba(236,34,41,0.15)', color: '#EC2229', fontSize: '0.75rem', fontWeight: 500 }}>optional</span>}
                      <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>{set.options?.length} Optionen</span>
                    </div>
                    <div style={{ padding: '6px 14px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {set.options?.map((opt, j) => {
                        const optKey = `${i}-${j}`;
                        const isSelected = selectedSetOptions[optKey];
                        return (
                          <label key={j} style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: 8,
                            background: isSelected ? 'rgba(236,34,41,0.08)' : 'rgba(255,255,255,0.03)',
                            borderRadius: 6, border: isSelected ? '1px solid rgba(236,34,41,0.4)' : '1px solid rgba(255,255,255,0.06)',
                            cursor: 'pointer', transition: 'all 0.15s ease',
                          }} data-testid={`pd-set-option-${i}-${j}`}
                            onMouseOver={e => { if (!isSelected) e.currentTarget.style.borderColor = 'rgba(236,34,41,0.25)'; }}
                            onMouseOut={e => { if (!isSelected) e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; }}
                          >
                            <input type="checkbox" checked={!!isSelected}
                              onChange={() => setSelectedSetOptions(prev => ({ ...prev, [optKey]: !prev[optKey] }))}
                              style={{ accentColor: '#EC2229', width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }}
                              data-testid={`pd-set-checkbox-${i}-${j}`} />
                            {opt.image_url && (
                              <img src={opt.image_url} alt={opt.name} style={{
                                width: 48, height: 48, objectFit: 'cover', borderRadius: 4, flexShrink: 0, background: '#222'
                              }} />
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ color: '#ddd', fontSize: '0.85rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt.name}</div>
                              {opt.details && <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>{opt.details}</div>}
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              {opt.price > 0 && <div style={{ color: '#fff', fontWeight: 600, fontSize: '0.85rem' }}>{formatPrice(opt.price)}</div>}
                              {opt.compare_price > 0 && <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.78rem', textDecoration: 'line-through' }}>{formatPrice(opt.compare_price)}</div>}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {/* Show total with selected options */}
                {Object.values(selectedSetOptions).some(v => v) && (
                  <div style={{
                    padding: '10px 14px', borderRadius: 8, background: 'rgba(236,34,41,0.08)',
                    border: '1px solid rgba(236,34,41,0.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }} data-testid="pd-sets-total">
                    <span style={{ color: '#ccc', fontSize: '0.85rem' }}>Gesamtpreis inkl. Zubehör:</span>
                    <span style={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>
                      {formatPrice((selectedVariant?.price || product.price || 0) + product.product_sets.reduce((sum, set, i) =>
                        sum + (set.options || []).reduce((s, opt, j) => s + (selectedSetOptions[`${i}-${j}`] ? (opt.price || 0) : 0), 0), 0)
                      )}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* VIN / Fahrgestellnummer - mandatory for specific products */}
            {product.requires_vin && (
              <div className="pd-vin-field" data-testid="vin-field">
                <label className="pd-vin-label">Fahrgestellnummer <span style={{ color: '#EC2229' }}>*</span></label>
                <div className="pd-vin-input-wrap">
                  <input
                    type="text"
                    className={`pd-vin-input${vinError ? ' error' : ''}`}
                    data-testid="vin-input"
                    placeholder="z.B. WVWZZZ3CZWE123456"
                    maxLength={17}
                    value={vin}
                    onChange={e => {
                      const val = e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');
                      setVin(val);
                      if (vinError) setVinError('');
                    }}
                  />
                  <span className="pd-vin-counter">{vin.length}/17 Zeichen</span>
                </div>
                {vinError && <span className="pd-vin-error" data-testid="vin-error">{vinError}</span>}
              </div>
            )}

            {/* Add to Cart + Speed Checkout */}
            {notAvailable && (
              <div className="pd-not-available" data-testid="pd-not-available"
                style={{ color: '#f87171', fontWeight: 600, margin: '8px 0 12px', padding: '10px 14px', background: 'rgba(239,68,68,0.1)', borderRadius: 8, border: '1px solid rgba(239,68,68,0.3)' }}>
                {product.grail_managed && !grailSelected ? "Bitte eine Ausführung wählen." : "Dieser Artikel ist zurzeit nicht lieferbar und kann aktuell nicht bestellt werden."}
              </div>
            )}
            <div className="pd-cart" data-testid="pd-cart">
              <div className="qty-control">
                <button disabled={notAvailable} onClick={() => setQuantity(q => Math.max(1, q - 1))}><Minus size={16} /></button>
                <span>{quantity}</span>
                <button disabled={notAvailable} onClick={() => setQuantity(q => q + 1)}><Plus size={16} /></button>
              </div>
              <button className="btn-add-cart" data-testid="add-to-cart-btn"
                disabled={notAvailable}
                onClick={() => {
                  if (notAvailable) return;
                  if (product.requires_vin && vin.length !== 17) {
                    setVinError('Bitte geben Sie eine gültige 17-stellige Fahrgestellnummer ein.');
                    return;
                  }
                  // Varianten-Pflichtfelder prüfen
                  if (product.variants?.length > 0) {
                    if (isMultiAttribute) {
                      const missing = variantAttributes.filter(a => !selectedAttrs[a.key]);
                      if (missing.length > 0) {
                        setVariantError(`Bitte wählen: ${missing.map(m => m.key).join(', ')}`);
                        return;
                      }
                    } else if (!selectedVariant) {
                      setVariantError('Bitte Variante auswählen');
                      return;
                    }
                  }
                  setVariantError('');
                  addToCart(product.grail_managed ? product.slug : selectedVariant?.slug || product.slug, quantity, (product.grail_managed ? grailSelected?.alias : selectedVariant?.alias) || null, product.requires_vin ? vin : null, getSelectedOptionsForCart());
                }}>
                <ShoppingCart size={18} /> In den Warenkorb
              </button>
            </div>
            {variantError && (
              <div style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: 4, padding: '6px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: 6, border: '1px solid rgba(239,68,68,0.3)' }} data-testid="variant-error">
                {variantError}
              </div>
            )}
            <button className="btn-speed-checkout" data-testid="speed-checkout-btn"
              disabled={notAvailable}
              onClick={() => {
              if (notAvailable) return;
              if (product.requires_vin && vin.length !== 17) {
                setVinError('Bitte geben Sie eine gültige 17-stellige Fahrgestellnummer ein.');
                return;
              }
              if (product.variants?.length > 0) {
                if (isMultiAttribute) {
                  const missing = variantAttributes.filter(a => !selectedAttrs[a.key]);
                  if (missing.length > 0) {
                    setVariantError(`Bitte wählen: ${missing.map(m => m.key).join(', ')}`);
                    return;
                  }
                } else if (!selectedVariant) {
                  setVariantError('Bitte Variante auswählen');
                  return;
                }
              }
              setVariantError('');
              addToCart(product.grail_managed ? product.slug : selectedVariant?.slug || product.slug, quantity, (product.grail_managed ? grailSelected?.alias : selectedVariant?.alias) || null, product.requires_vin ? vin : null, getSelectedOptionsForCart());
              navigate('/checkout');
            }}>
              <Zap size={16} /> Jetzt kaufen
            </button>

            <RegisterCTA variant="inline" />

            {/* REMUS Konfigurator – für alle Remus-Produkte mit related_aliases (modulare Komponenten) */}
            {(product.manufacturer === 'REMUS Innovation GmbH' || product.brand === 'REMUS')
              && Array.isArray(product.related_aliases) && product.related_aliases.length > 0 && (
              <RemusKonfigurator
                product={product}
                onAddToCart={async (item, qty) => {
                  try {
                    return await addToCart(item.slug, qty || 1, item.alias || null, null, []);
                  } catch (e) { console.error(e); }
                }}
              />
            )}

            {/* Friedrich Motorsport Konfigurator - Visual Preview + Fallback (v253)
                Wird nur angezeigt, wenn NOCH KEINE Kind-Varianten materialisiert sind
                (has_customizer=true aber variants leer). Sobald Kinder existieren,
                uebernehmen die native PDP-Multi-Dim-Dropdowns oben (Material/Endrohr/Schuerze). */}
            {product.has_customizer
              && (product.manufacturer === 'Friedrich Motorsport' || product.brand === 'Friedrich Motorsport')
              && !(product.variants?.length > 0) && (
              <FriedrichKonfigurator product={product} />
            )}

            {/* Installation Service */}
            {product.installation_available && (
              <div className="pd-installation" data-testid="installation-service">
                {!installOpen && !installSent ? (
                  <button className="btn-installation" onClick={() => setInstallOpen(true)} data-testid="install-inquiry-btn">
                    <Wrench size={16} /> Einbau anfragen {product.installation_note && <span className="install-note">— {product.installation_note}</span>}
                  </button>
                ) : installSent ? (
                  <div className="install-success">Einbau-Anfrage gesendet! Wir melden uns mit den Kosten.</div>
                ) : (
                  <div className="install-form-wrap">
                    <h4><Wrench size={16} /> Einbau-Kosten anfragen</h4>
                    <form onSubmit={handleInstallInquiry} className="install-form">
                      <input type="text" placeholder="Ihr Name" value={installForm.name} onChange={e => setInstallForm(f => ({ ...f, name: e.target.value }))} required />
                      <input type="email" placeholder="E-Mail *" value={installForm.email} onChange={e => setInstallForm(f => ({ ...f, email: e.target.value }))} required data-testid="install-email" />
                      <input type="tel" placeholder="Telefon" value={installForm.phone} onChange={e => setInstallForm(f => ({ ...f, phone: e.target.value }))} />
                      <textarea rows={2} placeholder="Nachricht (Fahrzeug, Wünsche...)" value={installForm.message} onChange={e => setInstallForm(f => ({ ...f, message: e.target.value }))} />
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button type="submit" className="btn-primary" data-testid="submit-install-btn">Anfrage senden</button>
                        <button type="button" className="btn-secondary" onClick={() => setInstallOpen(false)}>Abbrechen</button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            )}

            {/* Conversion Boosters */}
            {!product.grail_managed && !product.csr_display && !product.asr_managed && <DeliveryEstimate deliveryTime={product.delivery_time} stock={product.stock} />}

            {/* Gewinnspiel + Lossystem Promo */}
            <RafflePromoMini />

            {/* Angebot anfordern */}
            <div className="pd-quote-request" data-testid="quote-request-section" style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(255,255,255,0.05)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)' }}>
              {!quoteOpen && !quoteSent ? (
                <button data-testid="quote-request-btn" onClick={() => setQuoteOpen(true)}
                  style={{ width: '100%', padding: '10px 16px', borderRadius: 8, border: '1px solid #EC2229', background: 'transparent', color: '#EC2229', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <Info size={16} /> Individuelles Angebot anfordern
                </button>
              ) : quoteSent ? (
                <div style={{ textAlign: 'center', color: '#16a34a', fontWeight: 600, padding: 8 }}>
                  Ihre Anfrage wurde gesendet! Wir melden uns in Kürze.
                </div>
              ) : (
                <form onSubmit={handleQuoteRequest} style={{ display: 'grid', gap: 8 }}>
                  <h4 style={{ color: '#fff', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Info size={16} style={{ color: '#EC2229' }} /> Angebot anfordern
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <input type="text" placeholder="Name *" required value={quoteForm.name} onChange={e => setQuoteForm(f => ({ ...f, name: e.target.value }))}
                      style={{ padding: 8, borderRadius: 6, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: '#fff' }} data-testid="quote-name" />
                    <input type="email" placeholder="E-Mail *" required value={quoteForm.email} onChange={e => setQuoteForm(f => ({ ...f, email: e.target.value }))}
                      style={{ padding: 8, borderRadius: 6, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: '#fff' }} data-testid="quote-email" />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <input type="tel" placeholder="Telefon" value={quoteForm.phone} onChange={e => setQuoteForm(f => ({ ...f, phone: e.target.value }))}
                      style={{ padding: 8, borderRadius: 6, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: '#fff' }} />
                    <input type="text" placeholder="Firma (optional)" value={quoteForm.company} onChange={e => setQuoteForm(f => ({ ...f, company: e.target.value }))}
                      style={{ padding: 8, borderRadius: 6, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: '#fff' }} />
                  </div>
                  <input type="text" placeholder="Fahrzeugdaten (optional)" value={quoteForm.vehicle_info} onChange={e => setQuoteForm(f => ({ ...f, vehicle_info: e.target.value }))}
                    style={{ padding: 8, borderRadius: 6, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: '#fff' }} />
                  <textarea rows={2} placeholder="Ihre Nachricht..." value={quoteForm.message} onChange={e => setQuoteForm(f => ({ ...f, message: e.target.value }))}
                    style={{ padding: 8, borderRadius: 6, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: '#fff', resize: 'vertical' }} data-testid="quote-message" />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="submit" style={{ flex: 1, padding: '8px 16px', borderRadius: 8, background: '#EC2229', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }} data-testid="quote-submit-btn">Anfrage senden</button>
                    <button type="button" onClick={() => setQuoteOpen(false)} style={{ padding: '8px 16px', borderRadius: 8, background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', cursor: 'pointer' }}>Abbrechen</button>
                  </div>
                </form>
              )}
            </div>

            {!product.grail_managed && !product.csr_display && !product.asr_managed && <StockUrgency stock={product.stock} />}
            <LiveViewers />
            <TrustBadges />
            <PaymentIcons />

            {/* Main characteristics */}
            {displayProduct.main_characteristics && (
              <div className="pd-chars" dangerouslySetInnerHTML={{ __html: displayProduct.main_characteristics }} />
            )}
          </div>
        </div>

        {/* NOVITEC / Premium-Marken: maßgeschneiderte Ultra-Premium-Story */}
        {isPremiumBrand && (
          <NovitecPremium product={product} images={allImages} />
        )}

        {/* APR/Dinan Immersive Produktstory (Weltmeister-Layout) – auch für Marken mit "Modern"-Design */}
        {useModernStory && (
          <AprStorySections product={product} />
        )}

        {(product.asr_content || product.asr_has_variants) && <AsrContent content={asrContent} needsSelection={product.asr_has_variants && !hgVariant} />}
      {/* Tabs */}
        <div className="pd-tabs" data-testid="pd-tabs">
          <div className="tab-nav">
            {(['description', 'details', ...((product.fitment_data?.length || hgHasFitment) ? ['fitment'] : []), ...(hgHasVideos ? ['hg-videos'] : []), 'reviews']).map(tab => (
              <button key={tab} className={activeTab === tab ? 'active' : ''} onClick={() => setActiveTab(tab)}>
                {tab === 'description' ? 'Beschreibung' : tab === 'details' ? 'Details' : tab === 'fitment' ? 'Passend für' : tab === 'hg-videos' ? 'Produktvideos' : 'Bewertungen'}
              </button>
            ))}
          </div>
          <div className="tab-content">
            {activeTab === 'description' && (
              <>
                <RemusHeroSounds product={product} />
                {product.grail_managed ? <GrailContent content={grailContent} delivery={grailDelivery} sanitize={sanitizeDescriptionHtml} /> : <div className="desc-content" dangerouslySetInnerHTML={{
                  __html: sanitizeDescriptionHtml(asrContent?.description || (product.asr_has_variants ? '<p>Bitte eine Ausführung wählen, um die zugehörige Beschreibung zu sehen.</p>' : product.long_description || product.description || '<p>Keine Beschreibung verfügbar.</p>'))
                }} />}
                <GaranLabelBadge alias={product.alias} />

                {/* Endrohr-Konfigurator Grid (Legacy 1-Dim / andere Duplex-Anlagen).
                    Bei Multi-Dim (v253 Friedrich Material x Endrohr x Schuerze) uebernehmen
                    die nativen PDP-Dropdowns - dieses Grid wuerde sonst 189 Kacheln zeigen. */}
                {product.variants?.length > 0
                  && (product.variation_attributes || []).includes('Endrohr')
                  && !isMultiAttribute && (
                  <div className="pd-endrohr-konfig" data-testid="pd-endrohr-konfig" style={{
                    marginTop: 32, padding: 24, borderRadius: 16,
                    background: 'rgba(220, 38, 38, 0.06)',
                    border: '1px solid rgba(220, 38, 38, 0.25)',
                  }}>
                    <h3 style={{marginTop: 0, marginBottom: 8, fontSize: 20}}>
                      Endrohr-Konfigurator
                    </h3>
                    <p style={{marginTop: 0, marginBottom: 20, opacity: 0.75, fontSize: 14}}>
                      Wähle das gewünschte Endrohr — der Preis passt sich automatisch an.
                    </p>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                      gap: 12,
                    }}>
                      {product.variants.map(v => {
                        const label = getVariantLabel(v) || v.name;
                        const isSel = selectedVariant?.alias === v.alias;
                        return (
                          <button
                            key={v.alias}
                            type="button"
                            data-testid={`endrohr-option-${v.alias}`}
                            onClick={() => { setSelectedVariant(v); setVariantError(''); }}
                            style={{
                              cursor: 'pointer', textAlign: 'left',
                              padding: 14, borderRadius: 12,
                              background: isSel ? 'rgba(220,38,38,0.15)' : 'rgba(255,255,255,0.03)',
                              border: `2px solid ${isSel ? 'rgb(220,38,38)' : 'rgba(255,255,255,0.08)'}`,
                              transition: 'all 0.15s',
                              color: 'inherit',
                            }}
                          >
                            {/* v312a-fix-18 variant-image-typeguard */}
                            {(typeof (v.image_url_full || v.image_url) === 'string' && (v.image_url_full || v.image_url)) ? (
                              <img src={v.image_url_full || v.image_url} alt={label}
                                   style={{width: '100%', height: 110, objectFit: 'cover',
                                           borderRadius: 8, marginBottom: 10,
                                           background: '#fff'}} />
                            ) : null}
                            <div style={{fontWeight: 600, fontSize: 14, marginBottom: 4}}>{label}</div>
                            <div style={{fontSize: 15, fontWeight: 700, color: 'rgb(220,38,38)'}}>
                              {formatPrice(v.price)}
                            </div>
                            {isSel && (
                              <div style={{fontSize: 11, marginTop: 6, opacity: 0.8}}>
                                ✓ Ausgewählt
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {selectedVariant && (
                      <p style={{marginTop: 16, marginBottom: 0, fontSize: 13, opacity: 0.75}}>
                        Aktuelle Auswahl: <strong>{getVariantLabel(selectedVariant) || selectedVariant.name}</strong>
                        {' — '}scrolle nach oben um in den Warenkorb zu legen.
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
            {activeTab === 'fitment' && (
              <>
                <HgFitment content={hgContent} />
                {hgVariant && hgHasFitment && !hgContent?.fitment_html && <p>Für diese Variante liegen noch keine geprüften HG-Fahrzeughinweise vor. Bitte frag uns vor der Bestellung nach der Passform.</p>}
                {!!product.fitment_data?.length && <FitmentTable data={product.fitment_data} />}
              </>
            )}
            {activeTab === 'hg-videos' && (hgContent?.videos?.length ? <HgVideos key={hgVariant?.alias || product.alias} content={hgContent} /> : <p>Für diese Variante liegt noch kein zugeordnetes Produktvideo vor.</p>)}
            {activeTab === 'details' && (
              <div>
                <table className="detail-table" data-testid="product-details-table">
                  <tbody>
                    {product.alias && <tr><td>Interne Artikelnummer</td><td>{displayProduct.alias}</td></tr>}
                    {product.manufacturer && <tr><td>Hersteller</td><td>{product.manufacturer}</td></tr>}
                    {displayProduct.manufacturer_sku && <tr><td>Hersteller-Artikelnummer</td><td>{displayProduct.manufacturer_sku}</td></tr>}
                    {displayProduct.ean && <tr><td>EAN</td><td>{displayProduct.ean}</td></tr>}
                    {displayProduct.weight && <tr><td>Gewicht</td><td>{displayProduct.weight} {displayProduct.weight_unit || 'kg'}</td></tr>}
                    <tr><td>Lieferzeit</td><td>{(product.grail_managed ? grailDelivery : product.csr_display?.delivery || (product.asr_managed ? asrDelivery : formatDeliveryText(product.delivery_time, product.stock)))}</td></tr>
                    {/* Custom Fields */}
                    {customFields.map((cf, i) => (
                      <tr key={i} data-testid={`custom-field-${i+1}`}><td>{cf.label}</td><td>{cf.value}</td></tr>
                    ))}
                  </tbody>
                </table>

                {/* Manufacturer Info - Pflicht DE (GPSR) */}
                <div className="pd-manufacturer-info" data-testid="manufacturer-info">
                  <h4><Info size={16} /> Herstellerinformationen</h4>
                  {product.gpsr?.manufacturer_name && <p><strong>{product.gpsr.manufacturer_name}</strong></p>}
                  {product.gpsr?.manufacturer_address && <p style={{whiteSpace: 'pre-line'}}>{product.gpsr.manufacturer_address}</p>}
                  {product.gpsr?.manufacturer_email && <p>E-Mail: <a href={`mailto:${product.gpsr.manufacturer_email}`} style={{color: 'var(--primary)'}}>{product.gpsr.manufacturer_email}</a></p>}
                  {typeof product.gpsr?.manufacturer_url === 'string' && product.gpsr.manufacturer_url && (
                    <a href={(typeof product.gpsr.manufacturer_url === 'string' && product.gpsr.manufacturer_url.startsWith('http')) ? product.gpsr.manufacturer_url : `https://${product.gpsr.manufacturer_url}`} target="_blank" rel="noopener noreferrer" className="manufacturer-link">
                      <ExternalLink size={14} /> Website des Herstellers
                    </a>
                  )}
                  {!product.gpsr?.manufacturer_name && product.manufacturer_info && <p style={{whiteSpace: 'pre-line'}}>{product.manufacturer_info}</p>}
                  {!product.gpsr?.manufacturer_name && !product.manufacturer_info && product.manufacturer && <p>{product.manufacturer}</p>}
                  {product.manufacturer_url && !product.gpsr?.manufacturer_url && (
                    <a href={product.manufacturer_url} target="_blank" rel="noopener noreferrer" className="manufacturer-link">
                      <ExternalLink size={14} /> Website des Herstellers
                    </a>
                  )}
                </div>

                {/* Legal Notice & Approval */}
                {(displayProduct.legal_notice || displayProduct.approval_info) && (
                  <div className="pd-legal-notice" data-testid="legal-notice">
                    <h4><AlertTriangle size={16} /> Rechtliche Hinweise & Zulassung</h4>
                    {displayProduct.legal_notice && <p>{displayProduct.legal_notice}</p>}
                    {displayProduct.approval_info && <p className="approval-info">{displayProduct.approval_info}</p>}
                  </div>
                )}
              </div>
            )}
            {activeTab === 'reviews' && (
              <ProductReviews productSlug={product.slug} productName={product.name} />
            )}
          </div>
        </div>

        {/* Remus / Bastuck spezifische Sektionen: Sounds, Dokumente, Fahrzeuge, Dazugehörige Produkte */}
        <RemusSections product={product} />

        {/* Fitment Check */}
        <FitmentCheck productSlug={product.slug} productName={product.name} />

        {/* APR/Dinan TÜV-legal kombinierbare Teile ("Das passt dazu") – nur APR-Familie (markenspezifische Daten) */}
        {isAprFamily && (
          <AprCombinableParts alias={product.alias} brand={product.manufacturer} />
        )}

        {/* Cross-Selling */}
        {isPremiumBrand && product.cross_selling_grouped?.length > 0 ? (
          <NovitecCrossSell groups={product.cross_selling_grouped}
                            vehicle={[product.vehicle_brand, product.vehicle_model].filter(Boolean).join(' ')} />
        ) : product.cross_selling?.length > 0 && (
          <div className="pd-cross-selling" data-testid="pd-cross-selling">
            <h2>Das könnte Ihnen auch gefallen</h2>
            <div className="product-grid cs-grid">
              {product.cross_selling.slice(0, 6).map(p => (
                <Link key={p.alias} to={`/p/${p.slug}`} className="product-card">
                  <div className="product-card-img">
                    <img src={cdnImage(p.image_url)} alt={p.name} loading="lazy"
                      onError={e => { e.target.src = '/images/placeholder.svg'; }} />
                  </div>
                  <div className="product-card-info">
                    {p.manufacturer && <span className="product-brand">{p.manufacturer}</span>}
                    <h3 className="product-name">{p.name}</h3>
                    <span className="product-price">{formatPrice(p.price)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Recently Viewed */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 1.5rem' }}>
        <RecentlyViewed currentSlug={slug} />
      </div>

      <Footer />
      <CookieConsent />
      <NewsletterPopup />
    </div>
  );
}

// v307a: Passend-fuer Tabelle (Bar-Tek-Style)
function FitmentTable({ data }) {
  const groups = Array.isArray(data) ? data : [];
  const [activeBrand, setActiveBrand] = React.useState(groups[0]?.brand || '');
  if (!groups.length) return <p style={{opacity:.6}}>Keine Fahrzeug-Kompatibilität hinterlegt.</p>;
  const current = groups.find(g => g.brand === activeBrand) || groups[0];
  return (
    <div data-testid="fitment-table" className="fitment-table" style={{color:'inherit'}}>
      {groups.length > 1 && (
        <div style={{display:'flex',gap:0,borderBottom:'2px solid rgba(255,255,255,0.08)',marginBottom:16}}>
          {groups.map(g => (
            <button key={g.brand} type="button"
              onClick={() => setActiveBrand(g.brand)}
              data-testid={`fitment-brand-${g.brand}`}
              style={{
                padding:'12px 24px', cursor:'pointer', background:'transparent', border:'none',
                borderBottom: g.brand === (current.brand) ? '3px solid rgb(220,38,38)' : '3px solid transparent',
                color: g.brand === current.brand ? 'rgb(220,38,38)' : 'inherit',
                fontWeight: 700, fontSize: 14, letterSpacing: '1px', textTransform: 'uppercase',
              }}>{g.brand}</button>
          ))}
        </div>
      )}
      <div style={{display:'grid',gap:8}}>
        {(current.vehicles || []).map((v, i) => (
          <div key={i} data-testid={`fitment-row-${i}`}
            style={{
              display:'grid',
              gridTemplateColumns:'minmax(140px,1fr) minmax(140px,1fr) minmax(200px,2fr)',
              gap:12, padding:'12px 16px',
              background: i%2 ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.01)',
              borderRadius:6, alignItems:'center', fontSize:14,
            }}>
            <div style={{color:'rgb(220,38,38)',fontWeight:700}} data-testid="fitment-model">▸ {[v.brand, v.model].filter(Boolean).join(' ') || '-'}</div>
            <div style={{opacity:.75}}>
              {v.chassis && <span>{v.chassis}</span>}
              {v.year_from && <span> | BJ {v.year_from}{v.year_to ? `-${v.year_to}` : '→'}</span>}
            </div>
            <div style={{fontWeight:600}}>
              {v.engine}
              {v.engine_code && <span style={{marginLeft:8,opacity:.7,fontWeight:400}}>{v.engine_code}</span>}
              {v.power_ps > 0 && <span style={{marginLeft:8,color:'rgb(220,38,38)'}}>| {v.power_ps} PS{v.power_kw > 0 ? ` (${v.power_kw} kW)` : ''}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

